'use client';

import {
  ReactElement,
  useState,
  useCallback,
  useEffect,
  useContext,
  useRef,
  CSSProperties,
} from 'react';
import cn from 'classnames';
import { useNavUI } from '@context/MainNavContext/NavigationState';
import TopRibbon from '@components/global/TopRibbon';
import PromoBar from '_components/PromoBar';
import { PromoBarDocumentData } from 'prismicio-types';

import useScrollThrottled from '@hooks/useScrollThrottled';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime';
import { usePathname } from 'next/navigation';
import { useLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';
import { FEATURE_FLAG_PROMO_BAR_SERVER_SIDE_ENABLED } from '@config/feature-flag';
import PrimaryNavBar from '_components/PrimaryNavBar';
import MegaMenuDesktop from '_components/MegaMenu/Desktop';
import MegaMenuMobile from '_components/MegaMenu/Mobile';

import styles from './Header.module.scss';

export interface HeaderProps {
  alternateLocaleUrl?: string;
  promoTickerMessages: PromoTicker[];
  promoBarData?: PromoBarDocumentData['messages'];
  hideTopRibbonAndStoreSelector?: boolean;
}

const Header = ({
  alternateLocaleUrl,
  promoTickerMessages,
  promoBarData,
  hideTopRibbonAndStoreSelector,
}: HeaderProps): ReactElement => {
  const { actions, isTop, headerHeight } = useNavUI();
  const { setHeaderHeight, setIsTop } = actions;
  const [hideHeader, setHideHeader] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Dual router compatibility
  const pagesRouter = useContext(RouterContext);
  const appRouterPathname = usePathname();
  const pathname = pagesRouter?.asPath || appRouterPathname || '/';

  useEffect(() => {
    const handleRouteChange = () => {
      if (pathname.includes('/returns/')) {
        setIsTop({ boolean: true });
      }
    };

    // Pages Router: listen to route change events
    if (pagesRouter?.events) {
      pagesRouter.events.on('routeChangeComplete', handleRouteChange);
      return () => {
        pagesRouter.events.off('routeChangeComplete', handleRouteChange);
      };
    }

    // App Router: just check on mount/pathname change
    handleRouteChange();
  }, [pathname, pagesRouter, setIsTop]);

  const [headerRef, setHeaderRef] = useState<HTMLElement | null>(null);
  const headerRefUpdate = useCallback((node: HTMLElement) => {
    setHeaderRef(node);
  }, []);
  // Same measured value the desktop mega menu uses: the drawer is
  // `position: fixed` under the header, so it needs the rendered bottom edge.
  const mobileMenuTop = headerHeight ?? 0;

  // The header is a sticky bar translated up by the promo bar's height in its
  // resting (scrolled-up) state, so the promo bar tucks out of view while the
  // nav sits flush at the top. Measure it rather than hardcoding, since the
  // height varies by breakpoint, promo bar variant, and message length.
  const [promoBarNode, setPromoBarNode] = useState<HTMLDivElement | null>(null);
  const [promoBarHeight, setPromoBarHeight] = useState(0);

  useLayoutEffect(() => {
    if (!promoBarNode) return undefined;

    const updatePromoBarHeight = () =>
      setPromoBarHeight(promoBarNode.offsetHeight);

    updatePromoBarHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updatePromoBarHeight);
      return () => window.removeEventListener('resize', updatePromoBarHeight);
    }

    const resizeObserver = new ResizeObserver(updatePromoBarHeight);
    resizeObserver.observe(promoBarNode);

    return () => resizeObserver.disconnect();
  }, [promoBarNode]);

  const onMegaMenuOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setHideHeader(false);
    }

    if (open) {
      setIsMobileNavOpen(false);
    }
  }, []);

  const MINIMUM_SCROLL = 100;
  useScrollThrottled(({ previousScrollPosition, currentScrollTop }) => {
    const isScrolledDown = previousScrollPosition < currentScrollTop;

    const isCloseToTop =
      window.pageYOffset !== undefined
        ? window.pageYOffset
        : (
            document.documentElement ||
            document.body.parentNode ||
            document.body
          ).scrollTop;

    setIsTop({ boolean: isCloseToTop <= 100 });

    const isBottom =
      window.innerHeight + window.scrollY >= document.body.offsetHeight;
    const isMinimumScrolled = currentScrollTop > MINIMUM_SCROLL;
    const isMegaMenuOpen = !!headerRef?.querySelector('[data-popup-open]');

    if (!isMegaMenuOpen && !isBottom && isMinimumScrolled) {
      setHideHeader(isScrolledDown);
    }
  });

  // The mega menu panel is portaled out of the header and pinned with
  // `position: fixed`, so it needs the header's rendered bottom edge, not its
  // height. The two diverge whenever the sticky header is translated up to tuck
  // the promo bar out of view, leaving the open menu floating below the nav bar
  // by exactly the tucked amount.
  const lastHeaderBottom = useRef<number | null>(null);
  const measureHeaderHeight = useCallback(() => {
    if (!headerRef) return;

    const headerBottom = Math.max(
      0,
      Math.round(headerRef.getBoundingClientRect().bottom)
    );

    if (lastHeaderBottom.current === headerBottom) return;

    lastHeaderBottom.current = headerBottom;
    setHeaderHeight({ height: headerBottom });
  }, [headerRef, setHeaderHeight]);

  // Re-measure whenever state that affects the header's rendered position changes.
  useLayoutEffect(() => {
    measureHeaderHeight();
  }, [
    measureHeaderHeight,
    headerRef?.clientHeight,
    promoBarHeight,
    isTop,
    hideHeader,
    hideTopRibbonAndStoreSelector,
  ]);

  // Register event listeners once per headerRef.
  useLayoutEffect(() => {
    if (!headerRef) return undefined;

    // The tuck is a transform transition, so the resting position is only known
    // once it settles. Ignore transitions bubbling up from the promo bar.
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== headerRef || event.propertyName !== 'transform') {
        return;
      }
      measureHeaderHeight();
    };

    headerRef.addEventListener('transitionend', handleTransitionEnd);
    window.addEventListener('resize', measureHeaderHeight);

    return () => {
      headerRef.removeEventListener('transitionend', handleTransitionEnd);
      window.removeEventListener('resize', measureHeaderHeight);
    };
  }, [headerRef, measureHeaderHeight]);

  return (
    <>
      <header
        className={cn(styles.header, {
          [styles.hidden]: hideHeader,
          [styles.isScrolledToTop]: isTop,
          [styles.hideTopRibbonAndStoreSelector]: hideTopRibbonAndStoreSelector,
        })}
        ref={headerRefUpdate}
        style={{ '--promo-bar-height': `${promoBarHeight}px` } as CSSProperties}
      >
        <div
          ref={setPromoBarNode}
          className={cn({
            [styles.hideTopRibbon]: hideTopRibbonAndStoreSelector,
          })}
        >
          {FEATURE_FLAG_PROMO_BAR_SERVER_SIDE_ENABLED ? (
            <PromoBar
              alternateLocaleUrl={alternateLocaleUrl}
              promoBarData={promoBarData}
            />
          ) : (
            <TopRibbon
              alternateLocaleUrl={alternateLocaleUrl}
              promoTickerMessages={promoTickerMessages}
            />
          )}
        </div>
        <PrimaryNavBar
          isMobileNavOpen={isMobileNavOpen}
          onMobileNavToggle={() => setIsMobileNavOpen((prev) => !prev)}
        />
        <MegaMenuDesktop onOpenChange={onMegaMenuOpenChange} />
      </header>
      <MegaMenuMobile
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        top={mobileMenuTop}
        alternateLocaleUrl={alternateLocaleUrl}
      />
    </>
  );
};

export default Header;
